import React from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useQuery, useApolloClient } from "@apollo/client";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  GET_PERMIT_DETAIL,
  GetPermitDetailData,
  GetPermitDetailVars,
  transformPermitDetailRaw,
  GET_PERMIT_STATS,
} from "common/graphql";
import {
  PermitDetailDialogContent,
  PermitDetailDialogSkeleton,
} from "@curbsuite/components/permits/PermitDetailDialog";
import { ApplicationReviewContent } from "@curbsuite/components/permits/ApplicationReviewDialog";
import ConcurrentEditingAlert from "@curbsuite/components/ConcurrentEditingAlert";
import { useSessionManager } from "@app.automotus.io/components/hooks";
import { sanitizeInternalPath } from "common/helpers";

export const PermitDetailPage: React.FC = () => {
  const { permitId } = useParams<{ permitId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const client = useApolloClient();

  const {
    isCurrentUserOldest,
    editingUserFullName,
    isInitialLoadInProgress: sessionLoading,
    currentUserSessionConfirmed,
  } = useSessionManager(permitId);

  const {
    data,
    loading: permitLoading,
    error,
  } = useQuery<GetPermitDetailData, GetPermitDetailVars>(GET_PERMIT_DETAIL, {
    variables: { id: permitId || "" },
    skip: !permitId,
    fetchPolicy: "cache-and-network",
  });

  const permit = data?.permit ? transformPermitDetailRaw(data.permit) : null;
  const loading = permitLoading || (permitId && sessionLoading && !error);

  const handleBack = () => {
    // `from` is only ever present for a same-tab navigation (it lives in in-memory history
    // state), so when it's there, prefer `navigate(-1)` — a real history pop, which correctly
    // preserves the browser's forward-navigation stack instead of pushing a duplicate entry.
    const from = (location.state as { from?: string } | null)?.from;
    if (from && from.includes("/permits")) {
      navigate(-1);
      return;
    }
    // Only ever set on a link opened in a new tab — `state` doesn't survive that, so this is the
    // fallback for restoring the exact list view when `from` above isn't available. Sanitized
    // since it's user-controllable (a crafted link's query param) and gets passed to navigate().
    const returnTo = sanitizeInternalPath(searchParams.get("returnTo"));
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate("/permits", { replace: true });
    }
  };

  const handleActionComplete = () => {
    client.refetchQueries({
      include: [GET_PERMIT_STATS, "GetPermits"],
    });
    handleBack();
  };

  const isPending = permit?.applicationStatus === "pending" || permit?.applicationStatus === "awaiting_additional_info";
  const isReviewEnabled = isPending && isCurrentUserOldest && currentUserSessionConfirmed;
  const heading = isPending ? "Review Application" : "Permit Details";

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 1 }}>
            Back
          </Button>
          <Typography variant="h5" fontWeight={600}>
            {heading}
          </Typography>
        </Box>

        {loading && !permit ? (
          <PermitDetailDialogSkeleton />
        ) : error ? (
          <Alert severity="error">
            <AlertTitle>Error</AlertTitle>
            Failed to load permit details: {error.message}
          </Alert>
        ) : !permit ? (
          <Alert severity="warning">
            <AlertTitle>Not Found</AlertTitle>
            No permit found with ID &ldquo;{permitId}&rdquo;.
          </Alert>
        ) : isReviewEnabled ? (
          <ApplicationReviewContent permit={permit} onActionComplete={handleActionComplete} />
        ) : (
          <PermitDetailDialogContent permit={permit} />
        )}
      </Stack>

      {!isCurrentUserOldest && currentUserSessionConfirmed && !sessionLoading && isPending && (
        <ConcurrentEditingAlert editingUserFullName={editingUserFullName} itemLabel="application" />
      )}
    </Container>
  );
};

export default PermitDetailPage;
